/**
 * Lazy data hook for the KPI Scorecard per-metric detail endpoint.
 * Called only when a MetricDrawer is opened — not eagerly on page load.
 * Fixture substitution (dev without a running backend) only happens when
 * VITE_SCORECARD_FIXTURE=true — a real network error in production always
 * surfaces as `error`, never as fabricated data.
 */
import { useEffect, useState } from 'react';

import { scorecard as scorecardApi } from '@/helpers/ApiHelper';
import { THROUGHPUT_DETAIL_FIXTURE } from '@/pages/scorecard/scorecard.fixture';
import type { ScorecardMetricDetail, ScorecardParams } from '@/types/scorecard';

const USE_FIXTURE = import.meta.env.VITE_SCORECARD_FIXTURE === 'true';

export function useScorecardMetric(
  metricKey: string | null,
  params: ScorecardParams = {},
) {
  const [data, setData] = useState<ScorecardMetricDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!metricKey) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    if (USE_FIXTURE) {
      const tid = setTimeout(() => {
        if (!cancelled) {
          setData(THROUGHPUT_DETAIL_FIXTURE);
          setIsLoading(false);
        }
      }, 400);
      return () => {
        cancelled = true;
        clearTimeout(tid);
      };
    }

    scorecardApi
      .getScorecardMetric(metricKey, params)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          // Fixture substitution is gated on the explicit dev flag ONLY.
          // Never fall back silently in prod — a real network error (Cloud Run
          // cold start, transient CORS/backend blip, etc.) must surface as an
          // error with data left null, not render fabricated metric detail.
          if (USE_FIXTURE) {
            setData(THROUGHPUT_DETAIL_FIXTURE);
            setError(null);
          } else {
            setError(e instanceof Error ? e : new Error(String(e)));
          }
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricKey, params.preset, params.start, params.end]);

  return { data, isLoading, error };
}
